import "./mod-env-module.css";

import { CurveEditor, type CurvePoint } from "./curve-editor.ts";
import { Dropdown } from "./dropdown.ts";
import { Knob } from "./knob.ts";
import {
  MIN_ATK_MS,
  MAX_ATK_MS,
  MIN_DEC_MS,
  MAX_DEC_MS,
  MIN_REL_MS,
  MAX_REL_MS,
  MAX_TOTAL_MS,
  formatMs,
  timeToX,
  xToTime,
} from "./env-module.ts";

// ModEnvModule — free-routing modulation envelope. Visually it's an ADSR
// editor (same 3-handle layout as the amp envelope) plus two extras:
//
//   • a destination dropdown on the title row that selects which engine
//     parameter the envelope modulates
//   • a `mod amt` knob (0–100 %) that scales the envelope's effect on the
//     destination — 0 = bypass, 1 = the engine's full per-destination
//     modulation depth (e.g. 5 octaves for FilterCutoff)
//
// All shape control happens here; the destination-specific scaling lives
// in the audio engine (`Params::mod_destination` + the per-destination
// match in `engine::process_one`).

/** Destination ids — must match the integers the engine's wasm bridge
 *  maps to `ModDestination` variants (see `mod_destination_from_index`
 *  in `crates/synth-engine/src/wasm.rs`). Order matters: the dropdown
 *  dispatches by index, so adding entries means updating both this
 *  list and the engine's match arm. LFO and mod-env share this table. */
export type ModDestId =
  | "none"
  | "pitch"
  | "osc1.shape"
  | "osc1.level"
  | "osc1.detune"
  | "osc2.shape"
  | "osc2.level"
  | "osc2.detune"
  | "filter.cutoff"
  | "filter.resonance"
  | "filter.drive"
  | "master.gain";

export const MOD_DEST_ENGINE_INDEX: Record<ModDestId, number> = {
  none: 0,
  pitch: 1,
  "osc1.shape": 2,
  "osc1.level": 3,
  "osc1.detune": 4,
  "osc2.shape": 5,
  "osc2.level": 6,
  "osc2.detune": 7,
  "filter.cutoff": 8,
  "filter.resonance": 9,
  "filter.drive": 10,
  "master.gain": 11,
};

export const MOD_DEST_OPTIONS: {
  value: ModDestId;
  label: string;
  hint?: string;
}[] = [
  { value: "none", label: "none" },
  { value: "filter.cutoff", label: "filter cutoff", hint: "5 oct" },
  { value: "filter.resonance", label: "filter resonance" },
  { value: "filter.drive", label: "filter drive" },
  { value: "pitch", label: "pitch", hint: "±2 semi" },
  { value: "osc1.shape", label: "osc1 shape" },
  { value: "osc1.level", label: "osc1 level" },
  { value: "osc1.detune", label: "osc1 detune", hint: "±100¢" },
  { value: "osc2.shape", label: "osc2 shape" },
  { value: "osc2.level", label: "osc2 level" },
  { value: "osc2.detune", label: "osc2 detune", hint: "±100¢" },
  { value: "master.gain", label: "master gain" },
];

export interface ModEnvState {
  attackMs: number;
  decayMs: number;
  sustain: number;
  releaseMs: number;
  amount: number;
  destination: ModDestId;
}

export interface ModEnvModuleOptions {
  container: HTMLElement;
  /** Display title. Defaults to "mod env". */
  title?: string;
  /** Engine param-path prefix. Defaults to "mod_env"; the module fires
   *  `${pathBase}.attack_ms` etc., plus `mod_amount` and `mod_destination`. */
  pathBase?: string;
  setParam: (path: string, value: number) => void;
  setDiscrete: (path: string, value: number) => void;
  initial?: Partial<ModEnvState>;
}

export class ModEnvModule {
  private root: HTMLDivElement;
  private editor: CurveEditor;
  private attackKnob: Knob;
  private decayKnob: Knob;
  private sustainKnob: Knob;
  private releaseKnob: Knob;
  private amountKnob!: Knob;
  private destDropdown!: Dropdown<ModDestId>;

  private attackMs: number;
  private decayMs: number;
  private sustain: number;
  private releaseMs: number;

  private setParam: ModEnvModuleOptions["setParam"];
  private setDiscrete: ModEnvModuleOptions["setDiscrete"];
  private pathBase: string;

  constructor(opts: ModEnvModuleOptions) {
    const init = opts.initial ?? {};
    this.attackMs = init.attackMs ?? 5;
    this.decayMs = init.decayMs ?? 200;
    this.sustain = init.sustain ?? 0;
    this.releaseMs = init.releaseMs ?? 200;
    const initAmount = init.amount ?? 0;
    const initDest: ModDestId = init.destination ?? "none";

    this.setParam = opts.setParam;
    this.setDiscrete = opts.setDiscrete;
    this.pathBase = opts.pathBase ?? "mod_env";

    this.root = document.createElement("div");
    this.root.className = "mod-env-module";

    // Header — title on the left, destination dropdown on the right.
    const header = document.createElement("div");
    header.className = "mod-env-module-header";
    const title = document.createElement("span");
    title.className = "mod-env-module-title";
    title.textContent = opts.title ?? "mod env";
    header.append(title);

    const destHost = document.createElement("div");
    destHost.className = "mod-env-module-dest";
    header.append(destHost);
    this.destDropdown = new Dropdown<ModDestId>({
      container: destHost,
      label: "dest",
      options: MOD_DEST_OPTIONS,
      value: initDest,
      minWidth: 180,
      onChange: (value) => {
        this.setDiscrete("mod_destination", MOD_DEST_ENGINE_INDEX[value]);
      },
    });

    this.root.append(header);

    // Editor — three-handle ADSR, same layout as EnvModule's.
    const editorHost = document.createElement("div");
    editorHost.className = "mod-env-module-editor";
    this.root.append(editorHost);

    const pts = this.computePoints();
    this.editor = new CurveEditor({
      container: editorHost,
      width: 360,
      height: 120,
      fluid: true,
      points: pts,
      draw: (ctx, w, h, p) => this.drawEnv(ctx, w, h, p),
      onChange: (p) => this.onHandleDrag(p),
    });

    // Knob row — atk / dec / sus / rel | mod amt.
    const knobs = document.createElement("div");
    knobs.className = "mod-env-module-knobs";
    this.root.append(knobs);

    this.attackKnob = new Knob({
      container: knobs,
      size: 44,
      min: MIN_ATK_MS,
      max: MAX_ATK_MS,
      default: 5,
      value: this.attackMs,
      taper: "log",
      label: "atk",
      format: formatMs,
      onChange: (v) => {
        this.attackMs = v;
        this.pushEngine("attack_ms", v);
        this.syncEditorFromState();
      },
    });
    this.decayKnob = new Knob({
      container: knobs,
      size: 44,
      min: MIN_DEC_MS,
      max: MAX_DEC_MS,
      default: 200,
      value: this.decayMs,
      taper: "log",
      label: "dec",
      format: formatMs,
      onChange: (v) => {
        this.decayMs = v;
        this.pushEngine("decay_ms", v);
        this.syncEditorFromState();
      },
    });
    this.sustainKnob = new Knob({
      container: knobs,
      size: 44,
      min: 0,
      max: 1,
      default: 0,
      value: this.sustain,
      label: "sus",
      format: (v) => v.toFixed(2),
      onChange: (v) => {
        this.sustain = v;
        this.pushEngine("sustain", v);
        this.syncEditorFromState();
      },
    });
    this.releaseKnob = new Knob({
      container: knobs,
      size: 44,
      min: MIN_REL_MS,
      max: MAX_REL_MS,
      default: 200,
      value: this.releaseMs,
      taper: "log",
      label: "rel",
      format: formatMs,
      onChange: (v) => {
        this.releaseMs = v;
        this.pushEngine("release_ms", v);
        this.syncEditorFromState();
      },
    });

    // Mod amount lives in its own wrapper so the CSS can draw a divider
    // between the four ADSR knobs and this one — "envelope shape | depth".
    const amountHost = document.createElement("div");
    amountHost.className = "mod-env-module-amount";
    knobs.append(amountHost);
    this.amountKnob = new Knob({
      container: amountHost,
      size: 44,
      min: -1,
      max: 1,
      default: 0,
      value: initAmount,
      label: "mod amt",
      // Bipolar: −100 % inverts the modulation direction (envelope
      // attack closes the filter / lowers level / etc.), +100 % applies
      // the natural direction, 0 bypasses.
      format: (v) => {
        const pct = Math.round(v * 100);
        return pct > 0 ? `+${pct}%` : `${pct}%`;
      },
      onChange: (v) => this.setParam("mod_amount", v),
    });

    opts.container.append(this.root);
  }

  getState(): ModEnvState {
    return {
      attackMs: this.attackMs,
      decayMs: this.decayMs,
      sustain: this.sustain,
      releaseMs: this.releaseMs,
      amount: this.amountKnob.current,
      destination: this.destDropdown.getValue() ?? "none",
    };
  }

  setState(s: ModEnvState): void {
    this.attackKnob.setValue(s.attackMs);
    this.decayKnob.setValue(s.decayMs);
    this.sustainKnob.setValue(s.sustain);
    this.releaseKnob.setValue(s.releaseMs);
    this.amountKnob.setValue(s.amount);
    this.destDropdown.setValue(s.destination);
  }

  destroy(): void {
    this.root.remove();
  }

  private pushEngine(suffix: string, value: number): void {
    this.setParam(`${this.pathBase}.${suffix}`, value);
  }

  private computePoints(): CurvePoint[] {
    const ax = timeToX(this.attackMs);
    const dx = timeToX(this.attackMs + this.decayMs);
    const rx = timeToX(this.attackMs + this.decayMs + this.releaseMs);
    return [
      {
        x: ax,
        y: 1.0,
        drag: "x",
        xRange: [timeToX(MIN_ATK_MS), timeToX(MAX_ATK_MS)],
        yRange: [1, 1],
        lockNeighborsX: true,
        label: "attack",
      },
      {
        x: dx,
        y: this.sustain,
        drag: "xy",
        xRange: [
          timeToX(MIN_ATK_MS + MIN_DEC_MS),
          timeToX(MAX_ATK_MS + MAX_DEC_MS),
        ],
        yRange: [0, 1],
        lockNeighborsX: true,
        label: "decay/sustain",
      },
      {
        x: rx,
        y: 0,
        drag: "x",
        xRange: [
          timeToX(MIN_ATK_MS + MIN_DEC_MS + MIN_REL_MS),
          timeToX(MAX_TOTAL_MS),
        ],
        yRange: [0, 0],
        lockNeighborsX: true,
        label: "release",
      },
    ];
  }

  private syncEditorFromState(): void {
    const p = this.editor.getPoints();
    if (p.length < 3) return;
    p[0]!.x = timeToX(this.attackMs);
    p[1]!.x = timeToX(this.attackMs + this.decayMs);
    p[1]!.y = this.sustain;
    p[2]!.x = timeToX(this.attackMs + this.decayMs + this.releaseMs);
    this.editor.setPoints(p);
  }

  private onHandleDrag(p: CurvePoint[]): void {
    const attack = p[0]!;
    const decay = p[1]!;
    const release = p[2]!;
    const newAttack = clamp(xToTime(attack.x), MIN_ATK_MS, MAX_ATK_MS);
    const tDecayEnd = xToTime(decay.x);
    const newDecay = clamp(tDecayEnd - newAttack, MIN_DEC_MS, MAX_DEC_MS);
    const tReleaseEnd = xToTime(release.x);
    const newRelease = clamp(
      tReleaseEnd - newAttack - newDecay,
      MIN_REL_MS,
      MAX_REL_MS,
    );
    const newSustain = clamp(decay.y, 0, 1);

    if (Math.abs(newAttack - this.attackMs) > 1e-3) {
      this.attackMs = newAttack;
      this.attackKnob.setValue(newAttack);
      this.pushEngine("attack_ms", newAttack);
    }
    if (Math.abs(newDecay - this.decayMs) > 1e-3) {
      this.decayMs = newDecay;
      this.decayKnob.setValue(newDecay);
      this.pushEngine("decay_ms", newDecay);
    }
    if (Math.abs(newRelease - this.releaseMs) > 1e-3) {
      this.releaseMs = newRelease;
      this.releaseKnob.setValue(newRelease);
      this.pushEngine("release_ms", newRelease);
    }
    if (Math.abs(newSustain - this.sustain) > 1e-3) {
      this.sustain = newSustain;
      this.sustainKnob.setValue(newSustain);
      this.pushEngine("sustain", newSustain);
    }
  }

  private drawEnv(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    pts: CurvePoint[],
  ): void {
    const attack = pts[0]!;
    const decay = pts[1]!;
    const release = pts[2]!;
    ctx.save();
    ctx.shadowColor = "rgba(56, 189, 248, 0.55)";
    ctx.shadowBlur = 12;
    ctx.strokeStyle = "rgba(125, 211, 252, 0.9)";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(0, h);
    ctx.lineTo(attack.x * w, (1 - attack.y) * h);
    ctx.lineTo(decay.x * w, (1 - decay.y) * h);
    ctx.lineTo(release.x * w, (1 - release.y) * h);
    ctx.stroke();
    ctx.restore();
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
